import React, { ReactNode, useEffect, useState } from 'react';
import PercentageByDifficulty from '@components/progress/breakdown/micro/PercentageByDifficulty';
import Star from '@assets/icons/star';
import { useStudentWatchTime } from '@hooks/student.hook';

export interface BreakdownContentProps {
  skillLevel: number;
  lastPractised: string;
  watchTime: number;
  questionAttempted: number;
  easy: number;
  medium: number;
  hard: number;
  overall_percentage: number;
  total_easy_attempt: number;
  total_medium_attempt: number;
  total_hard_attempt: number;
  topicId: string;
}

const BreakdownContent = (props: BreakdownContentProps) => {
  const [watchTime, setWatchTime] = useState<number>(props.watchTime);
  const fetchStudentWatchTime = useStudentWatchTime({
    topic_id: props.topicId,
  });

  useEffect(() => {
    const response = fetchStudentWatchTime.data?.data;
    if (response?.error === false) {
      setWatchTime(response.data.watch_time);
    }
  }, [fetchStudentWatchTime.data?.data]);

  const formatWatchTime = (watchTime: number): string => {
    if (watchTime < 60) return '-';
    else if (watchTime < 3600) {
      const minutes = Math.floor(watchTime / 60);
      return `${minutes} min` + (minutes > 1 ? 's' : '');
    } else {
      // More than 60 minutes, display in hours and minutes
      const totalHours = Math.floor(watchTime / 3600); // Convert seconds to hours
      const remainingMinutes = watchTime % 3600; // Get remaining minutes after hours
      const minutes = Math.floor(remainingMinutes / 60); // Calculate minutes from remaining seconds
      return (
        `${totalHours}hr` +
        (totalHours > 1 ? 's' : '') +
        ` ${minutes} min` +
        (minutes > 1 ? 's' : '')
      );
    }
  };

  const detailItem: { item: string; description: ReactNode }[] = [
    {
      item: 'Skill Level',
      description: (
        <p>
          {props.skillLevel}
          <span
            className={
              'fill-current text-alert-warning w-4 inline-block ml-1 align-baseline'
            }
          >
            <Star />
          </span>
        </p>
      ),
    },
    {
      item: 'Last practised',
      description: props.lastPractised,
    },
    {
      item: 'Class watch time',
      description: formatWatchTime(watchTime),
    },
  ];

  return (
    <div className={'text-neutral-chalk'}>
      <div className={'mb-6'}>
        {detailItem.map(({ item, description }) => {
          return (
            <div
              key={item}
              className={
                'flex justify-between mb-2 last-of-type:mb-0 text-sm leading-4'
              }
              data-testid={item}
            >
              <div>{item}</div>
              <div>{description}</div>
            </div>
          );
        })}
      </div>
      <div
        className={'font-bold mb-2 text-neutral-chalk text-sm leading-[1.2]'}
      >
        Worksheet Performance
      </div>
      <div
        className={
          'w-[50%] rounded-2xl p-3 inline-block bg-primary-navyblue-shade/[0.65] font-normal text-xs text-neutral-whiteboard leading-[1.2]'
        }
      >
        Questions (marked) <br />
        <div className="font-extrabold text-sm text-neutral-chalk mt-2">
          {props.questionAttempted}
        </div>
      </div>
      <div className="w-[3%] inline-block"></div>
      <div
        className={
          'w-[47%] rounded-2xl p-3 inline-block bg-primary-navyblue-shade/[0.65] text-neutral-whiteboard font-normal text-xs leading-[1.2]'
        }
      >
        Overall accuracy <br />
        <div className="font-extrabold text-sm mt-2 text-neutral-chalk">
          {props.overall_percentage}%
        </div>
      </div>
      <div>
        <PercentageByDifficulty
          difficulty={'Easy'}
          percentage={props.easy}
          attempt={props.total_easy_attempt}
          className={'mb-4'}
        />
        <PercentageByDifficulty
          difficulty={'Medium'}
          percentage={props.medium}
          attempt={props.total_medium_attempt}
          className={'mb-4'}
        />
        <PercentageByDifficulty
          difficulty={'Hard'}
          percentage={props.hard}
          attempt={props.total_hard_attempt}
        />
      </div>
    </div>
  );
};

export default BreakdownContent;
